import type { WriteSetting } from '@myrewards/contracts';
import { useQuery, useSuspenseQuery } from '@tanstack/react-query';
import { useEffect, useRef, useState, type ReactElement, type ReactNode } from 'react';
import { Link } from 'react-aria-components';
import { useLocation } from 'react-router';
import { isApiError, meQuery } from '../api';
import { sectorSetupListQuery, settingHistoryQuery } from '../api/settings';
import { opensPage } from '../app/access';
import { initialsOf, TwoColumns, type HeroStat } from '../shell';
import {
  Button,
  Card,
  ConfirmDialog,
  FieldWithAction,
  Icon,
  MoneyField,
  StatusPill,
  TextField,
  cx,
  formatCount,
  plural,
  formatDate,
  scoped,
} from '../ui';
import { NotYourArea, SettingsFrame, type Section } from './Frame';
import { SettingHistoryDrawer, StepChart } from './history';
import {
  fmvNumber,
  fmvText,
  formatPerShare,
  isFraction,
  leadChange,
  optionShareText,
  organizationToday,
  paaExample,
  percentText,
  reduce,
  stepPoints,
  type Fraction,
} from './model';
import { NumberInput } from './NumberInput';
import { canOpenArea } from './nav';
import {
  ChangedLine,
  ScrollBox,
  SettingFallback,
  WhoEdits,
  useSetting,
  useSettingWrite,
  type SettingState,
} from './parts';
import { SettingConfirm, useSaveFor } from './SettingConfirm';
import s from './Settings.module.css';

const FMV = 'values.fmvCents';
// APP-151: a PAA's options as a share of its cash (the PAA cash/options split card is gone), and
// the CFO's yearly value of one option they're worked out at.
const PAA_OPTIONS = 'values.paaOptionShare';
const OPTION_VALUE = 'values.optionValueCents';
const MIN_OPTIONS = 'awards.minOptionsPerPerson';
/** The minimum is one number for both discretionary bonuses: written to both in one request (R73). */
const BOTH_MINIMUMS: Pick<WriteSetting, 'key' | 'scope' | 'alsoScopes'> = {
  key: MIN_OPTIONS,
  scope: 'SDB',
  alsoScopes: [{ scope: 'EDB' }],
};
// Lists kept in tables by the Admin server (APP-044); settings read them as projections.
const SECTOR_LEADS = 'directory.sectorLeads';

const DATE = /^\d{4}-\d{2}-\d{2}$/;

/** The FMV card: where the hero's "Add the FMV" leads (APP-093). */
const FMV_ANCHOR = 'fmv';

function FmvCard(): ReactElement | null {
  const today = organizationToday();
  const state = useSetting(FMV);
  const history = useQuery({ ...settingHistoryQuery(FMV), enabled: state.status === 'ready' });
  const { save, error, clearError } = useSettingWrite();
  const saveFor = useSaveFor();
  const [historyOpen, setHistoryOpen] = useState(false);
  const [cents, setCents] = useState<number | null>(null);
  const [from, setFrom] = useState('');
  const [confirming, setConfirming] = useState(false);
  const formRef = useRef<HTMLFormElement>(null);
  const location = useLocation();
  const ready = state.status === 'ready';
  // The hero's "Add the FMV" lands on the first field of the form (a same-page link keeps the
  // scroll where it was).
  useEffect(() => {
    const form = formRef.current;
    if (!ready || location.hash !== `#${FMV_ANCHOR}` || form === null) return;
    // jsdom has no scrollIntoView; focus still moves.
    if (typeof form.scrollIntoView === 'function') form.scrollIntoView({ block: 'center' });
    form.querySelector('input')?.focus({ preventScroll: true });
  }, [location.hash, location.key, ready]);
  const help = {
    shows:
      'The one fair market value per share. Options on every award are worked out from it: value ÷ FMV, rounded down to whole options.',
    canDo:
      'Executives and the Stock Admin team add a new FMV with the date it takes effect. Awards already calculated keep the FMV they used.',
  };
  // Not this person's to see (QA-025): no card at all, rather than a dead one.
  if (state.status === 'hidden') return null;
  if (state.status !== 'ready')
    return (
      <Card title="FMV per share" help={help} helpAnchor="values-fmv">
        <SettingFallback state={state} />
      </Card>
    );
  const view = state.view;
  const rows = history.data?.entries ?? [];
  const points = stepPoints(rows, fmvNumber, today);
  const ahead = points.filter((p) => p.upcoming);
  const valid = cents !== null && cents > 0 && DATE.test(from);

  return (
    <Card
      id={FMV_ANCHOR}
      title="FMV per share"
      helpAnchor="values-fmv"
      help={help}
      aside={<WhoEdits canEdit={view.canEdit}>Executives and Stock Admins edit</WhoEdits>}
    >
      <ChangedLine
        change={view.lastChanged}
        onHistory={() => {
          setHistoryOpen(true);
        }}
      />
      <div className={s.fmvRow}>
        <div className={s.fmvNow}>
          <p className={cx('eyebrow', s.eyebrowMuted)}>Areté stock · FMV in effect today</p>
          <p className={cx('disp', s.bigValue)}>{fmvText(view.value)}</p>
          {view.effectiveFrom !== null && (
            <p className={s.muted}>Effective {formatDate(view.effectiveFrom)}</p>
          )}
        </div>
        <div className={s.fmvChart}>
          {history.isError ? (
            <p className={s.muted} role="alert">
              The FMV history didn’t load. Reload the page to try again.
            </p>
          ) : history.isSuccess ? (
            // Before the first FMV, a ghost of the line on this year's axis (APP-093).
            <StepChart
              points={points}
              format={formatPerShare}
              label="FMV per share over time"
              today={today}
              empty={
                typeof view.value === 'number'
                  ? {
                      // Nothing saved: the built-in FMV applies until the first one is added.
                      headline: 'No FMV saved yet.',
                      body: view.canEdit
                        ? `${fmvText(view.value)} applies until the first FMV you add below; the line starts there.`
                        : `${fmvText(view.value)} applies until the first FMV is added; the line starts there.`,
                    }
                  : {
                      headline: 'No FMV yet.',
                      body: view.canEdit
                        ? 'The line starts with the first FMV you add below.'
                        : 'The line starts with the first FMV.',
                    }
              }
            />
          ) : (
            <p className={s.muted}>Loading…</p>
          )}
        </div>
      </div>
      {ahead.map((p) => (
        <p key={p.date} className={s.notice}>
          <StatusPill tone="waiting" size="sm" label="Scheduled" /> {formatPerShare(p.value)} takes
          effect on {formatDate(p.date)}. Awards dated before then keep {fmvText(view.value)}.
        </p>
      ))}
      <p className={s.body}>
        One FMV. Options are worked out from it, except a PAA’s, which use the value of one option.
        Each award keeps the values it used.
      </p>
      {view.canEdit && (
        <form
          ref={formRef}
          className={s.panel}
          onSubmit={(e) => {
            e.preventDefault();
            if (valid) setConfirming(true);
          }}
        >
          <h3 className={s.panelTitle}>Add a new FMV with an effective date</h3>
          <FieldWithAction
            actions={
              <Button type="submit" variant="navy" isDisabled={!valid}>
                Add FMV
              </Button>
            }
          >
            <MoneyField
              label="FMV per share ($)"
              valueCents={cents}
              onChangeCents={setCents}
              wholeDollars={false}
              minCents={1}
              isRequired
            />
            <TextField
              label="Effective from"
              type="date"
              value={from}
              onChange={setFrom}
              isRequired
            />
          </FieldWithAction>
          <p className={s.muted}>
            Awards already calculated keep their FMV. New awards use the FMV in effect on their
            award date.
          </p>
        </form>
      )}
      <SettingConfirm
        subject="FMV per share"
        isOpen={confirming}
        onOpenChange={(open) => {
          setConfirming(open);
          if (!open) clearError();
        }}
        title="Add this FMV?"
        intro="New awards dated on or after the effective date use it. Awards already calculated keep theirs."
        summary={[
          {
            label: 'FMV per share',
            before: fmvText(view.value),
            value: cents === null ? '' : formatPerShare(cents),
          },
          { label: 'Takes effect', value: DATE.test(from) ? formatDate(from) : '' },
        ]}
        reasonRequired={view.reasonRequired}
        reasonLabel={view.reasonRequired ? 'Reason' : 'Reason (optional)'}
        confirmLabel="Add FMV"
        error={error}
        onConfirmFor={async (reason, actingFor, forName) => {
          if (!valid) return;
          await saveFor(
            {
              key: FMV,
              scope: 'system',
              value: cents,
              effectiveFrom: from,
              ...(reason !== '' && { reason }),
              actingFor,
            },
            'FMV added',
            forName,
          );
          setCents(null);
          setFrom('');
        }}
        onConfirm={async (reason) => {
          if (!valid) return false;
          const ok = await save(
            {
              key: FMV,
              scope: 'system',
              value: cents,
              effectiveFrom: from,
              ...(reason !== '' && { reason }),
            },
            'FMV added',
          );
          if (ok) {
            setCents(null);
            setFrom('');
          }
          return ok;
        }}
      />
      <SettingHistoryDrawer
        isOpen={historyOpen}
        onOpenChange={setHistoryOpen}
        settingKey={FMV}
        title="FMV per share"
        subject="History · Areté stock"
        format={fmvText}
        numeric={fmvNumber}
        formatNumber={formatPerShare}
        emptyChart={{ headline: 'No FMV yet.', body: 'The line starts with the first FMV.' }}
      />
    </Card>
  );
}

/**
 * The value of one option (APP-151): the CFO works it out from the FMV each year, and a PAA's
 * options are its cash × the options share ÷ this value. Dated like the FMV; no default.
 */
function OptionValueCard(): ReactElement | null {
  const today = organizationToday();
  const state = useSetting(OPTION_VALUE);
  const history = useQuery({
    ...settingHistoryQuery(OPTION_VALUE),
    enabled: state.status === 'ready',
  });
  const { save, error, clearError } = useSettingWrite();
  const [historyOpen, setHistoryOpen] = useState(false);
  const [cents, setCents] = useState<number | null>(null);
  const [from, setFrom] = useState('');
  const [confirming, setConfirming] = useState(false);
  const help = {
    shows:
      'The value of one option, from the CFO’s yearly option calculation. A PAA’s options are worked out with it: the cash × the options share ÷ this value, rounded down to whole options.',
    canDo:
      'The Stock Admin team and System Admins add a new value with the date it takes effect and a reason. Awards already worked out keep the value they used.',
  };
  if (state.status === 'hidden') return null;
  if (state.status !== 'ready')
    return (
      <Card title="Value of one option" help={help}>
        <SettingFallback state={state} />
      </Card>
    );
  const view = state.view;
  const rows = history.data?.entries ?? [];
  const points = stepPoints(rows, fmvNumber, today);
  const ahead = points.filter((p) => p.upcoming);
  const valid = cents !== null && cents > 0 && DATE.test(from);
  const set = typeof view.value === 'number';

  return (
    <Card
      title="Value of one option"
      help={help}
      aside={<WhoEdits canEdit={view.canEdit}>The Stock Admin team edits</WhoEdits>}
    >
      <ChangedLine
        change={view.lastChanged}
        onHistory={() => {
          setHistoryOpen(true);
        }}
      />
      <div className={s.fmvRow}>
        <div className={s.fmvNow}>
          <p className={cx('eyebrow', s.eyebrowMuted)}>Value of one option today</p>
          <p className={cx('disp', s.bigValue)}>{fmvText(view.value)}</p>
          {view.effectiveFrom !== null && set && (
            <p className={s.muted}>Effective {formatDate(view.effectiveFrom)}</p>
          )}
        </div>
        <div className={s.fmvChart}>
          {history.isError ? (
            <p className={s.muted} role="alert">
              The option value’s history didn’t load. Reload the page to try again.
            </p>
          ) : history.isSuccess ? (
            <StepChart
              points={points}
              format={formatPerShare}
              label="Value of one option over time"
              today={today}
              empty={{
                headline: 'No option value yet.',
                body: view.canEdit
                  ? 'The line starts with the first value you add below.'
                  : 'The line starts with the first value.',
              }}
            />
          ) : (
            <p className={s.muted}>Loading…</p>
          )}
        </div>
      </div>
      {ahead.map((p) => (
        <p key={p.date} className={s.notice}>
          <StatusPill tone="waiting" size="sm" label="Scheduled" /> {formatPerShare(p.value)} takes
          effect on {formatDate(p.date)}. PAAs dated before then keep {fmvText(view.value)}.
        </p>
      ))}
      <p className={s.body}>
        {set
          ? `Value of one option: ${fmvText(view.value)}${view.effectiveFrom === null ? '' : ` from ${formatDate(view.effectiveFrom)}`}. From the CFO’s yearly option calculation. PAA options are worked out with it.`
          : view.canEdit
            ? 'Not set. Add the value of one option from the CFO’s yearly option calculation; until then a PAA’s options can’t be worked out.'
            : 'Not set. The Stock Admin team adds it from the CFO’s yearly option calculation; until then a PAA’s options can’t be worked out.'}
      </p>
      {view.canEdit && (
        <form
          className={s.panel}
          onSubmit={(e) => {
            e.preventDefault();
            if (valid) setConfirming(true);
          }}
        >
          <h3 className={s.panelTitle}>Add a new option value with an effective date</h3>
          <FieldWithAction
            actions={
              <Button type="submit" variant="navy" isDisabled={!valid}>
                Add value
              </Button>
            }
          >
            <MoneyField
              label="Value of one option ($)"
              valueCents={cents}
              onChangeCents={setCents}
              wholeDollars={false}
              minCents={1}
              isRequired
            />
            <TextField
              label="Effective from"
              type="date"
              value={from}
              onChange={setFrom}
              isRequired
            />
          </FieldWithAction>
          <p className={s.muted}>
            PAAs already worked out keep their value. New PAAs use the value in effect on their
            award date.
          </p>
        </form>
      )}
      <ConfirmDialog
        isOpen={confirming}
        onOpenChange={(open) => {
          setConfirming(open);
          if (!open) clearError();
        }}
        title="Add this option value?"
        intro="PAAs dated on or after the effective date use it. Awards already worked out keep theirs."
        summary={[
          {
            label: 'Value of one option',
            before: fmvText(view.value),
            value: cents === null ? '' : formatPerShare(cents),
          },
          { label: 'Takes effect', value: DATE.test(from) ? formatDate(from) : '' },
        ]}
        reasonRequired={view.reasonRequired}
        reasonLabel={view.reasonRequired ? 'Reason' : 'Reason (optional)'}
        confirmLabel="Add value"
        error={error}
        onConfirm={async (reason) => {
          if (!valid) return false;
          const ok = await save(
            {
              key: OPTION_VALUE,
              scope: 'system',
              value: cents,
              effectiveFrom: from,
              ...(reason !== '' && { reason }),
            },
            'Option value added',
          );
          if (ok) {
            setCents(null);
            setFrom('');
          }
          return ok;
        }}
      />
      <SettingHistoryDrawer
        isOpen={historyOpen}
        onOpenChange={setHistoryOpen}
        settingKey={OPTION_VALUE}
        title="Value of one option"
        subject="History · Values"
        format={fmvText}
        numeric={fmvNumber}
        formatNumber={formatPerShare}
        emptyChart={{
          headline: 'No option value yet.',
          body: 'The line starts with the first value.',
        }}
      />
    </Card>
  );
}

/** The PAA options share (APP-151): a PAA's options are worth this share of its cash, at the FMV. */
function PaaOptionsCard({ optionValue }: { optionValue: SettingState }): ReactElement | null {
  const state = useSetting(PAA_OPTIONS, 'PAA');
  const { save, error, clearError } = useSettingWrite();
  const [historyOpen, setHistoryOpen] = useState(false);
  const [editing, setEditing] = useState(false);
  const [percent, setPercent] = useState<number | null>(null);
  const [from, setFrom] = useState('');
  const [confirming, setConfirming] = useState(false);
  const help = {
    shows:
      'How many options a PAA gives on top of its cash. The amount entered on a PAA is the cash the person gets; its options are worth this share of the cash, at the value of one option in force, rounded down to whole options. The whole PAA is paid from the PAA/TAA pool.',
    canDo:
      'The Stock Admin team and System Admins set a new share with the date it takes effect and a reason. Awards already worked out keep the share they used.',
  };
  if (state.status === 'hidden') return null;
  if (state.status !== 'ready')
    return (
      <Card title="PAA options" help={help} helpAnchor="values-split">
        <SettingFallback state={state} />
      </Card>
    );
  const view = state.view;
  const current = isFraction(view.value) ? view.value : null;
  const draft: Fraction | null =
    percent !== null && Number.isInteger(percent) && percent >= 0 && percent <= 100
      ? reduce({ num: percent, den: 100 })
      : null;
  const valueCents =
    optionValue.status === 'ready' && typeof optionValue.view.value === 'number'
      ? optionValue.view.value
      : null;
  const shown = editing && draft !== null ? draft : current;
  const example =
    shown !== null && valueCents !== null ? paaExample(500_000, shown, valueCents) : null;
  const valid = draft !== null && DATE.test(from);

  return (
    <Card
      title="PAA options"
      helpAnchor="values-split"
      help={help}
      aside={<WhoEdits canEdit={view.canEdit}>The Stock Admin team edits</WhoEdits>}
    >
      <ChangedLine
        change={view.lastChanged}
        onHistory={() => {
          setHistoryOpen(true);
        }}
      />
      <ScrollBox label="PAA options">
        <table className={s.table}>
          <caption className="visually-hidden">Options given on top of a PAA’s cash</caption>
          <thead>
            <tr>
              <th scope="col">Award type</th>
              <th scope="col" className={s.num}>
                Options worth
              </th>
              <th scope="col">Effective from</th>
              <th scope="col">Status</th>
              <th scope="col">
                <span className="visually-hidden">Actions</span>
              </th>
            </tr>
          </thead>
          <tbody>
            <tr>
              <th scope="row" className={s.rowHead}>
                Professional Achievement Award
              </th>
              <td className={s.num}>{optionShareText(current)}</td>
              <td>
                {view.effectiveFrom === null ? 'Since setup' : formatDate(view.effectiveFrom)}
              </td>
              <td>
                {current === null ? (
                  <StatusPill tone="neutral" size="sm" label="Not set yet" />
                ) : (
                  <StatusPill tone="done" size="sm" label="In use" />
                )}
              </td>
              <td className={s.actionsCell}>
                {view.canEdit && !editing && (
                  <Button
                    variant="ghost"
                    size="sm"
                    onPress={() => {
                      setPercent(
                        current === null ? null : Math.round((current.num / current.den) * 100),
                      );
                      setEditing(true);
                    }}
                  >
                    Edit
                  </Button>
                )}
              </td>
            </tr>
          </tbody>
        </table>
      </ScrollBox>
      {editing && (
        <form
          className={s.panel}
          onSubmit={(e) => {
            e.preventDefault();
            if (valid) setConfirming(true);
          }}
        >
          <h3 className={s.panelTitle}>New PAA options share</h3>
          <div className={s.fieldRow}>
            <NumberInput
              label="Options worth (% of the cash)"
              value={percent}
              onChange={setPercent}
              minValue={0}
              maxValue={100}
              isRequired
            />
            <TextField
              label="Effective from"
              type="date"
              value={from}
              onChange={setFrom}
              isRequired
            />
          </div>
          <p className={s.muted} aria-live="polite">
            {draft === null
              ? 'Enter a whole percent from 0 to 100.'
              : draft.num === 0
                ? 'At 0%, a PAA pays its cash and no options.'
                : `Options worth ${percentText(draft)} of the cash, at the value of one option.`}
          </p>
          <div className={s.formActions}>
            <Button type="submit" variant="navy" isDisabled={!valid}>
              Review change
            </Button>
            <Button
              variant="ghost"
              onPress={() => {
                setEditing(false);
              }}
            >
              Cancel
            </Button>
          </div>
        </form>
      )}
      {shown !== null && (
        <p className={s.body}>
          Options are worth {percentText(shown)} of the cash award, at the value of one option.
          Rounded down to whole options.{' '}
          {example !== null && valueCents !== null
            ? `A $5,000 award pays $5,000 cash and ${plural(example.options, 'option', 'options')} at ${formatPerShare(valueCents)} an option.`
            : 'The value of one option isn’t set yet, so there’s no example: add it in Value of one option above.'}{' '}
          The whole PAA, cash and options, is paid from the PAA/TAA pool. TAAs are cash only.
        </p>
      )}
      <ConfirmDialog
        isOpen={confirming}
        onOpenChange={(open) => {
          setConfirming(open);
          if (!open) clearError();
        }}
        title="Change PAA options?"
        intro="PAAs worked out on or after the effective date use it. Awards already worked out keep theirs."
        summary={[
          {
            label: 'Professional Achievement Award',
            before: optionShareText(view.value),
            value: draft === null ? '' : optionShareText(draft),
          },
          { label: 'Takes effect', value: DATE.test(from) ? formatDate(from) : '' },
        ]}
        reasonRequired={view.reasonRequired}
        reasonLabel={view.reasonRequired ? 'Reason' : 'Reason (optional)'}
        confirmLabel="Change PAA options"
        error={error}
        onConfirm={async (reason) => {
          if (!valid) return false;
          const ok = await save(
            {
              key: PAA_OPTIONS,
              scope: 'PAA',
              value: draft,
              effectiveFrom: from,
              ...(reason !== '' && { reason }),
            },
            'PAA options changed',
          );
          if (ok) setEditing(false);
          return ok;
        }}
      />
      <SettingHistoryDrawer
        isOpen={historyOpen}
        onOpenChange={setHistoryOpen}
        settingKey={PAA_OPTIONS}
        scope="PAA"
        title="PAA options"
        subject="History · Values"
        format={optionShareText}
        numeric={(v) => (isFraction(v) ? Math.round((v.num / v.den) * 10_000) / 100 : null)}
        formatNumber={(v) =>
          `${v.toLocaleString('en-US', { maximumFractionDigits: 2 })}% of the cash`
        }
      />
    </Card>
  );
}

function TaaSplitCard({ budgetsHref }: { budgetsHref: string }): ReactElement | null {
  const state = useSetting('taa.defaultSplit');
  if (state.status === 'hidden') return null;
  const help = {
    shows:
      'The default share of each TAA paid from the PAA/TAA pool; the rest comes from the sector pool. Each person is paid wholly from one pool.',
    canDo:
      'Executives and Stock Admins change it on Budget pools. An Executive can change the split on a single TAA.',
  };
  return (
    <Card title="Default TAA split" help={help} helpAnchor="values-taa-split">
      {state.status === 'ready' && isFraction(state.view.value) ? (
        <TaaSplitBar split={state.view.value}>
          By person: each person is paid wholly from one pool and the app assigns people to match.
          On one TAA an Executive can change the split, and Executives, Stock Admins and System
          Admins can move people between pools, logged.{' '}
          <Link href={budgetsHref} className={s.link}>
            Change the default on Budget pools
          </Link>
        </TaaSplitBar>
      ) : (
        <SettingFallback state={state.status === 'ready' ? { status: 'error' } : state} />
      )}
      {state.status === 'ready' && <ChangedLine change={state.view.lastChanged} />}
    </Card>
  );
}

/** "50% PAA/TAA pool · 50% sector pool" with its bar (Executive settings only). */
export function TaaSplitBar({
  split,
  children,
}: {
  split: Fraction;
  children?: ReactNode;
}): ReactElement {
  const paa = Math.round((split.num / split.den) * 1000) / 10;
  return (
    <div className={s.splitBlock}>
      <div className={s.splitNumbers}>
        <span>
          <span className={cx('disp', s.splitPct)}>{paa}%</span> PAA/TAA pool
        </span>
        <span>
          <span className={cx('disp', s.splitPct)}>{Math.round((100 - paa) * 10) / 10}%</span>{' '}
          sector pool
        </span>
      </div>
      <div className={s.splitBar} aria-hidden="true">
        <span className={s.splitPaa} style={{ flexGrow: paa }} />
        <span className={s.splitSector} style={{ flexGrow: 100 - paa }} />
      </div>
      {children !== undefined && <p className={s.body}>{children}</p>}
    </div>
  );
}

function MinOptionsCard(): ReactElement | null {
  const sdb = useSetting(MIN_OPTIONS, 'SDB');
  const edb = useSetting(MIN_OPTIONS, 'EDB');
  const { save, error, clearError } = useSettingWrite();
  const saveFor = useSaveFor();
  const [draft, setDraft] = useState<number | null>(null);
  const [confirming, setConfirming] = useState(false);
  const [historyOpen, setHistoryOpen] = useState(false);
  const help = {
    shows:
      'The fewest options a person can be given on a Sector or Executive Discretionary Bonus, when they’re given options at all.',
    canDo:
      'Executives and Stock Admins change it. Awards already entered keep the minimum they were checked with.',
  };
  if (sdb.status === 'hidden') return null;
  if (sdb.status !== 'ready')
    return (
      <Card title="Minimum options per person" help={help} helpAnchor="values-min-options">
        <SettingFallback state={sdb} />
      </Card>
    );
  const value = typeof sdb.view.value === 'number' ? sdb.view.value : 0;
  const edbValue =
    edb.status === 'ready' && typeof edb.view.value === 'number' ? edb.view.value : value;
  const shown = draft ?? value;
  const canEdit = sdb.view.canEdit;
  return (
    <Card title="Minimum options per person" help={help} helpAnchor="values-min-options">
      {canEdit ? (
        <div className={s.stepperField}>
          <NumberInput
            label="Options per person, at least"
            value={shown}
            onChange={setDraft}
            minValue={1}
            stepButtons
          />
        </div>
      ) : (
        <p className={cx('disp', s.bigValue)}>{formatCount(value)}</p>
      )}
      <p className={s.body}>When options are given on a Sector or Executive Discretionary Bonus.</p>
      {edbValue !== value && (
        <p className={s.notice}>
          The Executive Discretionary minimum is {formatCount(edbValue)}. Saving sets both to the
          same number.
        </p>
      )}
      <ChangedLine
        change={sdb.view.lastChanged}
        onHistory={() => {
          setHistoryOpen(true);
        }}
      />
      {canEdit && draft !== null && draft !== value && (
        <div className={s.formActions}>
          <Button
            variant="navy"
            isDisabled={!Number.isInteger(draft) || draft < 1}
            onPress={() => {
              setConfirming(true);
            }}
          >
            Review change
          </Button>
          <Button
            variant="ghost"
            onPress={() => {
              setDraft(null);
            }}
          >
            Cancel
          </Button>
        </div>
      )}
      <SettingConfirm
        subject="Minimum options per person"
        isOpen={confirming}
        onOpenChange={(open) => {
          setConfirming(open);
          if (!open) clearError();
        }}
        title="Change the minimum options?"
        intro="It applies to Sector and Executive Discretionary Bonuses entered from now on."
        summary={[
          {
            label: 'Minimum options per person',
            before: formatCount(value),
            value: draft === null ? '' : formatCount(draft),
          },
        ]}
        reasonRequired={sdb.view.reasonRequired}
        reasonLabel={sdb.view.reasonRequired ? 'Reason' : 'Reason (optional)'}
        confirmLabel="Change minimum"
        error={error}
        onConfirmFor={async (reason, actingFor, forName) => {
          if (draft === null) return;
          const extra = reason !== '' ? { reason } : {};
          // Both awards' minimum, one change, both or neither (R73).
          await saveFor(
            { ...BOTH_MINIMUMS, value: draft, ...extra, actingFor },
            'Minimum changed',
            forName,
          );
          setDraft(null);
        }}
        onConfirm={async (reason) => {
          if (draft === null) return false;
          const extra = reason !== '' ? { reason } : {};
          const saved = await save({ ...BOTH_MINIMUMS, value: draft, ...extra }, 'Minimum changed');
          if (saved) setDraft(null);
          return saved;
        }}
      />
      <SettingHistoryDrawer
        isOpen={historyOpen}
        onOpenChange={setHistoryOpen}
        settingKey={MIN_OPTIONS}
        scope="SDB"
        title="Minimum options per person"
        subject="History · Values"
        format={(v) => (typeof v === 'number' ? `${formatCount(v)} options` : 'Not set')}
        numeric={(v) => (typeof v === 'number' ? v : null)}
        formatNumber={(v) => formatCount(v)}
      />
    </Card>
  );
}

/** Whether a read failed because this person may not see it (404) or Preview is on (403). */
const notOpen = (error: unknown): boolean =>
  isApiError(error) && (error.status === 404 || error.status === 403);

/** Who leads each sector (X5): read-only here; a System Admin keeps the list in Roles & lists. */
function SectorLeadsCard(): ReactElement | null {
  const q = useQuery(sectorSetupListQuery);
  const changed = useSetting(SECTOR_LEADS);
  const [historyOpen, setHistoryOpen] = useState(false);
  const sectors = q.data?.sectors;
  // Nothing to show to someone who can't see any sector's setup (Payroll).
  if (q.isError ? notOpen(q.error) : sectors?.length === 0) return null;
  const help = {
    shows:
      'Who leads each sector, and so who approves delegate entries and gets that sector’s notices.',
    canDo:
      'View only. A System Admin keeps this list in Settings › System administration › Roles & lists.',
  };
  return (
    <Card
      title="Sector Leads"
      titleSize="sm"
      compact
      help={help}
      aside={<StatusPill tone="neutral" size="sm" label="Read-only" />}
    >
      {sectors === undefined ? (
        <SettingFallback state={q.isError ? { status: 'error' } : { status: 'loading' }} />
      ) : (
        <ul className={s.people} aria-label="Sector Leads">
          {sectors.map((sector) => (
            <li key={sector.id} className={s.person}>
              <span className={s.avatar} aria-hidden="true">
                {sector.lead === null ? (
                  <Icon name="person" size={18} />
                ) : (
                  initialsOf(sector.lead.name)
                )}
              </span>
              <span className={s.personName}>
                <span className={s.strong}>{sector.lead?.name ?? 'No Sector Lead yet'}</span>
                <span className={s.sub}>{sector.name}</span>
              </span>
            </li>
          ))}
        </ul>
      )}
      <p className={s.footnote}>Kept by a System Admin in Roles &amp; lists.</p>
      {changed.status === 'ready' && (
        <ChangedLine
          change={changed.view.lastChanged}
          onHistory={() => {
            setHistoryOpen(true);
          }}
        />
      )}
      <SettingHistoryDrawer
        isOpen={historyOpen}
        onOpenChange={setHistoryOpen}
        settingKey={SECTOR_LEADS}
        title="Sector Leads"
        subject="History · Roles & lists"
        format={leadChange}
        listChanges
      />
    </Card>
  );
}

/** Values (X5): FMV, the option value and PAA options share, minimum options, requesters. */
export function ValuesPage({ section }: { section: Section }): ReactElement {
  const { data: me } = useSuspenseQuery(meQuery);
  // Executive › Values: the Executives', and System Admins see every page (APP-134).
  const allowed =
    section === 'executive' ? opensPage(me, ['executive']) : canOpenArea(me, 'values');
  // Someone the page isn't for gets the refusal straight away, without asking for anything.
  const fmv = useSetting(FMV, 'system', allowed);
  const optionValue = useSetting(OPTION_VALUE, 'system', allowed);
  if (!allowed) return <NotYourArea area={section === 'executive' ? 'executive' : 'values'} />;
  const budgetsHref =
    section === 'executive' ? '/manage/executive/budget-pools' : '/settings/budgets';
  const fmvLabel = scoped('Areté stock', 'FMV per share today');
  // No FMV yet: the way to add it when this person can, or who adds it (APP-093).
  const fmvStat: HeroStat | null =
    fmv.status !== 'ready'
      ? null
      : typeof fmv.view.value === 'number'
        ? {
            label: fmvLabel,
            value: fmvText(fmv.view.value),
            ...(fmv.view.effectiveFrom !== null && {
              meta: `Effective ${formatDate(fmv.view.effectiveFrom)}`,
            }),
          }
        : fmv.view.canEdit
          ? {
              label: fmvLabel,
              value: 'Not set',
              notSet: {
                message: 'Add the first FMV below',
                actionLabel: 'Add the FMV',
                href: `#${FMV_ANCHOR}`,
              },
            }
          : {
              label: fmvLabel,
              value: 'Not set',
              meta: 'Executives and Stock Admins add the FMV',
              tone: 'muted',
            };
  return (
    <SettingsFrame
      section={section}
      hero={{
        eyebrow: section === 'executive' ? 'Executive · Values' : 'Settings · Values',
        title: 'Values',
        sub: 'FMV, the value of one option and the options a PAA gives on top of its cash, minimum options and who can request an Executive Discretionary Bonus. The default TAA split is set on Budget pools.',
        stats: fmvStat === null ? [] : [fmvStat],
      }}
    >
      <TwoColumns
        sideLabel="Requesters"
        main={
          <>
            <FmvCard />
            <OptionValueCard />
            <PaaOptionsCard optionValue={optionValue} />
            <div className={s.pair}>
              <TaaSplitCard budgetsHref={budgetsHref} />
              <MinOptionsCard />
            </div>
          </>
        }
        side={
          <>
            <SectorLeadsCard />
          </>
        }
      />
    </SettingsFrame>
  );
}

/** Settings › Values (Stock Admins edit the FMV, the option value and the PAA options share; Payroll views). */
export function SettingsValuesPage(): ReactElement {
  return <ValuesPage section="settings" />;
}

/** Executive › Values (X5). */
export function ExecutiveValuesPage(): ReactElement {
  return <ValuesPage section="executive" />;
}
